// src/api/dashboardApi.js
import api from "./api";

/**
 * Dashboard API service
 * Provides methods for accessing dashboard endpoints
 */

// ==================== ADMIN DASHBOARD ====================

/**
 * Get admin dashboard overview
 * @returns {Promise} Admin overview data with metrics, task stats, and recent activity
 */
export const getAdminOverview = async () => {
  const response = await api.get("/dashboard/admin/overview");
  return response.data.data;
};

/**
 * Get top performers
 * @param {string} period - Time period: "week", "month", or "year"
 * @param {number} limit - Number of top performers to return (default: 10)
 * @returns {Promise} Top performers ranking
 */
export const getTopPerformers = async (period = "month", limit = 10) => {
  const response = await api.get("/dashboard/admin/top-performers", {
    params: { period, limit },
  });
  return response.data.data;
};

/**
 * Get admin activity feed
 * @param {number} limit - Number of activities to return (default: 50)
 * @returns {Promise} Recent activity feed
 */
export const getAdminActivityFeed = async (limit = 50) => {
  const response = await api.get("/dashboard/admin/activity-feed", {
    params: { limit },
  });
  return response.data.data;
};

// ==================== USER DASHBOARD ====================

/**
 * Get user dashboard overview
 * @returns {Promise} User dashboard data with stats, performance, and recent tasks
 */
export const getUserDashboardOverview = async () => {
  const response = await api.get("/dashboard/overview");
  return response.data.data;
};

/**
 * Get user task statistics
 * @returns {Promise} User task stats
 */
export const getUserTaskStats = async () => {
  const response = await api.get("/dashboard/my-stats");
  return response.data.data;
};

/**
 * Get user performance metrics
 * @returns {Promise} User performance metrics
 */
export const getUserPerformance = async () => {
  const response = await api.get("/dashboard/my-performance");
  return response.data.data;
};

/**
 * Get user recent tasks
 * @param {number} limit - Number of tasks to return (default: 10)
 * @returns {Promise} Recent tasks list
 */
export const getUserRecentTasks = async (limit = 10) => {
  const response = await api.get("/dashboard/my-recent-tasks", {
    params: { limit },
  });
  return response.data.data;
};

/**
 * Get user activity history
 * @param {number} limit - Number of activities to return (default: 20)
 * @returns {Promise} User activity history
 */
export const getUserActivity = async (limit = 20) => {
  const response = await api.get("/dashboard/my-activity", {
    params: { limit },
  });
  return response.data.data;
};

// ==================== ACTIVITY LOGGING ====================

/**
 * Log user activity manually
 * @param {Object} activityData - Activity data
 * @param {string} activityData.activity_type - Type of activity
 * @param {string} activityData.entity_id - Related entity ID (optional)
 * @param {string} activityData.entity_type - Related entity type (optional)
 * @param {Object} activityData.meta_data - Additional metadata (optional)
 * @returns {Promise} Created activity log
 */
export const logActivity = async (activityData) => {
  const response = await api.post("/dashboard/log-activity", activityData);
  return response.data.data;
};
